/**
 * Color Harmony Service
 *
 * Implements color compatibility using color theory principles:
 * - LAB color space for perceptually uniform color distance
 * - Euclidean distance calculations
 * - Color harmony categories (complementary, analogous, triadic, etc.)
 * - Fashion-specific rules and weighted scoring
 */

export interface ColorRGB {
  r: number;
  g: number;
  b: number;
}

export interface ColorLAB {
  l: number; // Lightness (0-100)
  a: number; // Green-Red axis (-128 to 127)
  b: number; // Blue-Yellow axis (-128 to 127)
}

export interface ColorHSL {
  h: number; // Hue (0-360)
  s: number; // Saturation (0-100)
  l: number; // Lightness (0-100)
}

export interface ColorCompatibilityResult {
  score: number; // 0-1 compatibility score
  harmony: ColorHarmonyType;
  distance: number; // LAB distance
  explanation: string[];
  confidence: number;
  method: "color-theory";
}

export type ColorHarmonyType =
  | "complementary"
  | "analogous"
  | "triadic"
  | "tetradic"
  | "monochromatic"
  | "neutral"
  | "clashing"
  | "split-complementary";

export interface FashionContext {
  garmentTypes?: string[];
  season?: "spring" | "summer" | "fall" | "winter";
  occasion?: "casual" | "business" | "formal" | "party";
}

export class ColorHarmonyService {
  private readonly NEUTRAL_COLORS = [
    { r: 0, g: 0, b: 0 }, // Black
    { r: 255, g: 255, b: 255 }, // White
    { r: 128, g: 128, b: 128 }, // Gray
    { r: 64, g: 64, b: 64 }, // Dark gray
    { r: 192, g: 192, b: 192 }, // Light gray
    { r: 139, g: 69, b: 19 }, // Brown
    { r: 160, g: 82, b: 45 }, // Saddle brown
    { r: 210, g: 180, b: 140 }, // Tan
    { r: 222, g: 184, b: 135 }, // Burlywood
  ];

  private readonly SEASONAL_PALETTES = {
    spring: [
      { r: 255, g: 192, b: 203 }, // Pink
      { r: 255, g: 182, b: 193 }, // Light pink
      { r: 255, g: 160, b: 122 }, // Light salmon
      { r: 255, g: 218, b: 185 }, // Peach puff
      { r: 240, g: 230, b: 140 }, // Khaki
    ],
    summer: [
      { r: 135, g: 206, b: 235 }, // Sky blue
      { r: 173, g: 216, b: 230 }, // Light blue
      { r: 176, g: 224, b: 230 }, // Powder blue
      { r: 255, g: 250, b: 205 }, // Lemon chiffon
      { r: 240, g: 248, b: 255 }, // Alice blue
    ],
    fall: [
      { r: 139, g: 69, b: 19 }, // Brown
      { r: 160, g: 82, b: 45 }, // Saddle brown
      { r: 210, g: 105, b: 30 }, // Chocolate
      { r: 255, g: 140, b: 0 }, // Dark orange
      { r: 255, g: 165, b: 0 }, // Orange
    ],
    winter: [
      { r: 25, g: 25, b: 112 }, // Midnight blue
      { r: 0, g: 0, b: 139 }, // Dark blue
      { r: 75, g: 0, b: 130 }, // Indigo
      { r: 128, g: 0, b: 128 }, // Purple
      { r: 255, g: 0, b: 255 }, // Magenta
    ],
  };

  /**
   * Convert RGB to LAB color space
   */
  private rgbToLab(rgb: ColorRGB): ColorLAB {
    // First convert RGB to XYZ
    let rNorm = rgb.r / 255;
    let gNorm = rgb.g / 255;
    let bNorm = rgb.b / 255;

    // Apply gamma correction
    rNorm =
      rNorm > 0.04045 ? Math.pow((rNorm + 0.055) / 1.055, 2.4) : rNorm / 12.92;
    gNorm =
      gNorm > 0.04045 ? Math.pow((gNorm + 0.055) / 1.055, 2.4) : gNorm / 12.92;
    bNorm =
      bNorm > 0.04045 ? Math.pow((bNorm + 0.055) / 1.055, 2.4) : bNorm / 12.92;

    // Convert to XYZ using sRGB matrix
    const x = rNorm * 0.4124564 + gNorm * 0.3575761 + bNorm * 0.1804375;
    const y = rNorm * 0.2126729 + gNorm * 0.7151522 + bNorm * 0.072175;
    const z = rNorm * 0.0193339 + gNorm * 0.119192 + bNorm * 0.9503041;

    // Normalize by D65 illuminant
    const xn = x / 0.95047;
    const yn = y / 1.0;
    const zn = z / 1.08883;

    // Convert to LAB
    const fx = xn > 0.008856 ? Math.pow(xn, 1 / 3) : 7.787 * xn + 16 / 116;
    const fy = yn > 0.008856 ? Math.pow(yn, 1 / 3) : 7.787 * yn + 16 / 116;
    const fz = zn > 0.008856 ? Math.pow(zn, 1 / 3) : 7.787 * zn + 16 / 116;

    const l = 116 * fy - 16;
    const a = 500 * (fx - fy);
    const b = 200 * (fy - fz);

    return { l, a, b };
  }

  /**
   * Convert RGB to HSL
   */
  private rgbToHsl(rgb: ColorRGB): ColorHSL {
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const diff = max - min;

    let h = 0;
    if (diff !== 0) {
      if (max === r) {
        h = ((g - b) / diff) % 6;
      } else if (max === g) {
        h = (b - r) / diff + 2;
      } else {
        h = (r - g) / diff + 4;
      }
    }
    h = Math.round(h * 60);
    if (h < 0) h += 360;

    const l = (max + min) / 2;
    const s = diff === 0 ? 0 : diff / (1 - Math.abs(2 * l - 1));

    return {
      h,
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    };
  }

  /**
   * Calculate Euclidean distance in LAB color space
   */
  private calculateLabDistance(color1: ColorLAB, color2: ColorLAB): number {
    const deltaL = color1.l - color2.l;
    const deltaA = color1.a - color2.a;
    const deltaB = color1.b - color2.b;

    return Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB);
  }

  /**
   * Determine color harmony type based on HSL values
   */
  private determineHarmonyType(
    hsl1: ColorHSL,
    hsl2: ColorHSL
  ): ColorHarmonyType {
    const hueDiff = Math.abs(hsl1.h - hsl2.h);
    const normalizedHueDiff = Math.min(hueDiff, 360 - hueDiff);

    // Monochromatic (same hue, different saturation/lightness)
    if (normalizedHueDiff <= 15) {
      return "monochromatic";
    }

    // Complementary (opposite on color wheel)
    if (normalizedHueDiff >= 165 && normalizedHueDiff <= 195) {
      return "complementary";
    }

    // Split complementary (30 degrees from complementary)
    if (normalizedHueDiff >= 150 && normalizedHueDiff <= 210) {
      return "split-complementary";
    }

    // Analogous (adjacent colors)
    if (normalizedHueDiff <= 30) {
      return "analogous";
    }

    // Triadic (120 degrees apart)
    if (normalizedHueDiff >= 105 && normalizedHueDiff <= 135) {
      return "triadic";
    }

    // Tetradic (90 degrees apart)
    if (normalizedHueDiff >= 75 && normalizedHueDiff <= 105) {
      return "tetradic";
    }

    // Check for neutral colors
    if (this.isNeutralColor(hsl1) || this.isNeutralColor(hsl2)) {
      return "neutral";
    }

    // Clashing (colors that don't fit traditional harmony)
    return "clashing";
  }

  /**
   * Check if a color is neutral
   */
  private isNeutralColor(hsl: ColorHSL): boolean {
    return hsl.s <= 15 || hsl.l <= 15 || hsl.l >= 85;
  }

  /**
   * Check if colors are both neutrals
   */
  private areBothNeutrals(hsl1: ColorHSL, hsl2: ColorHSL): boolean {
    return this.isNeutralColor(hsl1) && this.isNeutralColor(hsl2);
  }

  /**
   * Check if colors are warm or cool
   */
  private getColorTemperature(hsl: ColorHSL): "warm" | "cool" | "neutral" {
    if (this.isNeutralColor(hsl)) return "neutral";

    // Warm colors: reds, oranges, yellows (0-60, 300-360)
    // Cool colors: greens, blues, purples (120-240)
    if ((hsl.h >= 0 && hsl.h <= 60) || (hsl.h >= 300 && hsl.h <= 360)) {
      return "warm";
    } else if (hsl.h >= 120 && hsl.h <= 240) {
      return "cool";
    }

    return "neutral";
  }

  /**
   * Calculate base compatibility score from LAB distance
   */
  private calculateBaseScore(
    distance: number,
    harmony: ColorHarmonyType
  ): number {
    // LAB distance thresholds for different harmony types
    const thresholds = {
      monochromatic: { max: 20, min: 5 },
      analogous: { max: 40, min: 15 },
      complementary: { max: 60, min: 30 },
      "split-complementary": { max: 50, min: 25 },
      triadic: { max: 55, min: 25 },
      tetradic: { max: 50, min: 20 },
      neutral: { max: 30, min: 0 },
      clashing: { max: 80, min: 40 },
    };

    const threshold = thresholds[harmony];

    if (distance <= threshold.min) {
      return 0.9; // Very close colors
    } else if (distance <= threshold.max) {
      // Linear interpolation within threshold
      return (
        0.9 -
        ((distance - threshold.min) / (threshold.max - threshold.min)) * 0.4
      );
    } else {
      return Math.max(0.1, 0.5 - (distance - threshold.max) * 0.01);
    }
  }

  /**
   * Apply fashion-specific rules and weighted scoring
   */
  private applyFashionRules(
    baseScore: number,
    harmony: ColorHarmonyType,
    hsl1: ColorHSL,
    hsl2: ColorHSL,
    context?: FashionContext
  ): { score: number; explanation: string[] } {
    let score = baseScore;
    const explanation: string[] = [];

    // Neutral colors are always highly compatible
    if (this.areBothNeutrals(hsl1, hsl2)) {
      score = Math.max(score, 0.95);
      explanation.push("Both colors are neutrals - excellent compatibility");
    } else if (this.isNeutralColor(hsl1) || this.isNeutralColor(hsl2)) {
      score = Math.max(score, 0.85);
      explanation.push("One neutral color - very versatile pairing");
    }

    // Black and white are universally compatible
    if ((hsl1.l <= 5 && hsl2.l >= 95) || (hsl1.l >= 95 && hsl2.l <= 5)) {
      score = Math.max(score, 0.98);
      explanation.push("Classic black and white combination");
    }

    // Warm vs cool pairing rules
    const temp1 = this.getColorTemperature(hsl1);
    const temp2 = this.getColorTemperature(hsl2);

    if (temp1 === "warm" && temp2 === "cool") {
      score *= 0.9; // Slightly reduce score for warm-cool combinations
      explanation.push(
        "Warm and cool color combination - requires careful balance"
      );
    } else if (temp1 === temp2 && temp1 !== "neutral") {
      score *= 1.1; // Boost score for same temperature
      explanation.push(`Both colors are ${temp1} - harmonious temperature`);
    }

    // Seasonal palette compatibility
    if (context?.season) {
      const seasonalBoost = this.checkSeasonalCompatibility(
        hsl1,
        hsl2,
        context.season
      );
      if (seasonalBoost > 0) {
        score += seasonalBoost;
        explanation.push(`Colors match ${context.season} seasonal palette`);
      }
    }

    // Occasion-specific rules
    if (context?.occasion) {
      const occasionAdjustment = this.getOccasionAdjustment(
        harmony,
        context.occasion
      );
      score *= occasionAdjustment;
      if (occasionAdjustment > 1) {
        explanation.push(`Harmony type suits ${context.occasion} occasions`);
      } else if (occasionAdjustment < 1) {
        explanation.push(
          `Harmony type may not be ideal for ${context.occasion} occasions`
        );
      }
    }

    // Garment type specific rules
    if (context?.garmentTypes) {
      const garmentAdjustment = this.getGarmentTypeAdjustment(
        harmony,
        context.garmentTypes
      );
      score *= garmentAdjustment;
    }

    // Ensure score stays within bounds
    score = Math.max(0, Math.min(1, score));

    return { score, explanation };
  }

  /**
   * Check seasonal palette compatibility
   */
  private checkSeasonalCompatibility(
    hsl1: ColorHSL,
    hsl2: ColorHSL,
    season: string
  ): number {
    const palette =
      this.SEASONAL_PALETTES[season as keyof typeof this.SEASONAL_PALETTES];
    if (!palette) return 0;

    // Convert HSL to RGB for comparison
    const rgb1 = this.hslToRgb(hsl1);
    const rgb2 = this.hslToRgb(hsl2);

    // Check if colors are close to seasonal palette
    const isInPalette1 = palette.some(
      (color) =>
        this.calculateLabDistance(this.rgbToLab(rgb1), this.rgbToLab(color)) <
        30
    );
    const isInPalette2 = palette.some(
      (color) =>
        this.calculateLabDistance(this.rgbToLab(rgb2), this.rgbToLab(color)) <
        30
    );

    if (isInPalette1 && isInPalette2) {
      return 0.15; // Both colors in seasonal palette
    } else if (isInPalette1 || isInPalette2) {
      return 0.08; // One color in seasonal palette
    }

    return 0;
  }

  /**
   * Convert HSL to RGB
   */
  private hslToRgb(hsl: ColorHSL): ColorRGB {
    const h = hsl.h / 360;
    const s = hsl.s / 100;
    const l = hsl.l / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
    const m = l - c / 2;

    let r = 0,
      g = 0,
      b = 0;

    if (h < 1 / 6) {
      r = c;
      g = x;
      b = 0;
    } else if (h < 2 / 6) {
      r = x;
      g = c;
      b = 0;
    } else if (h < 3 / 6) {
      r = 0;
      g = c;
      b = x;
    } else if (h < 4 / 6) {
      r = 0;
      g = x;
      b = c;
    } else if (h < 5 / 6) {
      r = x;
      g = 0;
      b = c;
    } else {
      r = c;
      g = 0;
      b = x;
    }

    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255),
    };
  }

  /**
   * Get occasion-specific adjustment
   */
  private getOccasionAdjustment(
    harmony: ColorHarmonyType,
    occasion: string
  ): number {
    const adjustments = {
      casual: {
        complementary: 1.0,
        analogous: 1.1,
        monochromatic: 1.0,
        neutral: 1.0,
        triadic: 0.9,
        tetradic: 0.8,
        "split-complementary": 1.0,
        clashing: 0.7,
      },
      business: {
        complementary: 0.9,
        analogous: 1.0,
        monochromatic: 1.2,
        neutral: 1.3,
        triadic: 0.8,
        tetradic: 0.7,
        "split-complementary": 0.9,
        clashing: 0.5,
      },
      formal: {
        complementary: 0.8,
        analogous: 0.9,
        monochromatic: 1.3,
        neutral: 1.4,
        triadic: 0.7,
        tetradic: 0.6,
        "split-complementary": 0.8,
        clashing: 0.4,
      },
      party: {
        complementary: 1.2,
        analogous: 1.0,
        monochromatic: 0.8,
        neutral: 0.7,
        triadic: 1.1,
        tetradic: 1.0,
        "split-complementary": 1.1,
        clashing: 1.0,
      },
    };

    return adjustments[occasion as keyof typeof adjustments]?.[harmony] || 1.0;
  }

  /**
   * Get garment type specific adjustment
   */
  private getGarmentTypeAdjustment(
    harmony: ColorHarmonyType,
    garmentTypes: string[]
  ): number {
    // Different garment types have different color compatibility expectations
    let adjustment = 1.0;

    // Outerwear (jackets, coats) can be more bold
    if (
      garmentTypes.some((type) =>
        ["jacket", "coat", "blazer"].includes(type.toLowerCase())
      )
    ) {
      if (["complementary", "triadic", "tetradic"].includes(harmony)) {
        adjustment *= 1.1;
      }
    }

    // Tops (shirts, blouses) should be more conservative
    if (
      garmentTypes.some((type) =>
        ["shirt", "blouse", "top"].includes(type.toLowerCase())
      )
    ) {
      if (["monochromatic", "analogous", "neutral"].includes(harmony)) {
        adjustment *= 1.1;
      }
    }

    // Accessories can be more experimental
    if (
      garmentTypes.some((type) =>
        ["accessory", "scarf", "belt"].includes(type.toLowerCase())
      )
    ) {
      adjustment *= 1.05; // Slight boost for accessories
    }

    return adjustment;
  }

  /**
   * Calculate confidence based on color properties
   */
  private calculateConfidence(
    harmony: ColorHarmonyType,
    distance: number,
    hsl1: ColorHSL,
    hsl2: ColorHSL
  ): number {
    let confidence = 0.8; // Base confidence

    // Higher confidence for well-defined harmony types
    if (
      ["complementary", "analogous", "monochromatic", "neutral"].includes(
        harmony
      )
    ) {
      confidence += 0.1;
    }

    // Lower confidence for clashing colors
    if (harmony === "clashing") {
      confidence -= 0.2;
    }

    // Higher confidence for neutral combinations
    if (this.areBothNeutrals(hsl1, hsl2)) {
      confidence += 0.1;
    }

    return Math.max(0.3, Math.min(1.0, confidence));
  }

  /**
   * Main method to calculate color compatibility
   */
  public calculateCompatibility(
    color1: ColorRGB,
    color2: ColorRGB,
    context?: FashionContext
  ): ColorCompatibilityResult {
    // Convert to LAB and HSL
    const lab1 = this.rgbToLab(color1);
    const lab2 = this.rgbToLab(color2);
    const hsl1 = this.rgbToHsl(color1);
    const hsl2 = this.rgbToHsl(color2);

    // Calculate distance and harmony
    const distance = this.calculateLabDistance(lab1, lab2);
    const harmony = this.determineHarmonyType(hsl1, hsl2);

    // Calculate base score
    const baseScore = this.calculateBaseScore(distance, harmony);

    // Apply fashion rules
    const { score, explanation } = this.applyFashionRules(
      baseScore,
      harmony,
      hsl1,
      hsl2,
      context
    );

    // Calculate confidence
    const confidence = this.calculateConfidence(harmony, distance, hsl1, hsl2);

    // Add harmony-specific explanation
    explanation.unshift(
      `${
        harmony.charAt(0).toUpperCase() + harmony.slice(1)
      } color harmony (LAB distance: ${distance.toFixed(1)})`
    );

    return {
      score,
      harmony,
      distance,
      explanation,
      confidence,
      method: "color-theory",
    };
  }
}

// Singleton instance
export const colorHarmonyService = new ColorHarmonyService();
